
import BarcodeLabel from '@/components/BarcodeLabel.vue'
import SkippingCustomValidationDialog from '@/components/SkippingCustomValidationDialog.vue'
import { BARCODE_ACTION_COMPLETE, BARCODE_SELECTED_TYPE, BULK_ACTIVATE_EXTERNAL } from '@/store/actions'
import { activateBarcode } from '@/utils/api'
import errorHandler, { errorComparisonHandler, processError } from '@/utils/errorHandler'
import { filterEmptyActivateData, hasComparisonError, isJson, toSnakeCase } from '@/utils/helpers'
import {
  IActivateRequestBody,
  IActivationCustomField,
  IActivationData,
  IAddress,
  IBarcodeFormMultiDataTab,
  IBarcodeFormSingleDataTab,
  TError,
} from 'smartbarcode-web-core/src/utils/types/index'
import { Options, Vue } from 'vue-class-component'
import { ProvideReactive, Watch } from 'vue-property-decorator'

@Options({
  components: {
    BarcodeLabel,
    SkippingCustomValidationDialog,
    name: 'BarcodeLabel',
  },
  methods: { toSnakeCase },
})
export default class ActivateView extends Vue {
  @ProvideReactive() isShowArrow = true
  loading = false
  showSkipValidationDialog = false
  skipValidationMessage = ''
  lastActivateRequest: IActivateRequestBody | null = null
  type = ''
  trackpoint = ''
  origin?: IAddress | null = null
  destination?: IAddress | null = null

  currentTab = 0
  steps = ['origin', 'destination', 'packageInfo']

  get barcodeId() {
    return this.$store.state.barcode.barcode?.id
  }

  get showStepper() {
    return this.$route.name.includes('shipping')
  }

  onChangeType(params: IBarcodeFormSingleDataTab) {
    const selectedType = params.data
    this.type = selectedType
    this.$store.commit(BARCODE_SELECTED_TYPE, this.type)

    if (this.bulkContext) {
      const parentBarcode = this.$store.state.barcode.barcode
      const parentData = parentBarcode?.activationData
      const isSameType = selectedType === parentBarcode?.barcodeType
      const targetActivationFields = this.$store.state.project.details?.barcodeTypes?.[selectedType]?.activationFields

      this.trackpoint = isSameType
        ? this.resolveTrackpointForBulk(selectedType, parentBarcode?.currentTrackPointKey)
        : ''

      this.origin = isSameType || targetActivationFields?.origin?.isAvailable ? parentData?.origin || null : null
      this.destination =
        isSameType || targetActivationFields?.destination?.isAvailable ? parentData?.destination || null : null
    } else {
      this.trackpoint = ''
      this.origin = null
      this.destination = null
    }

    this.gotoStep(params.step)
  }

  onChangeTrackpoint(params: IBarcodeFormSingleDataTab) {
    this.trackpoint = params.data
    this.gotoStep(params.step)
  }

  onChangeSource(params: IBarcodeFormMultiDataTab) {
    this.origin = params.origin
    this.gotoStep(params.step)
  }

  onChangeDestination(params: IBarcodeFormMultiDataTab) {
    this.destination = params.destination
    this.gotoStep(params.step)
  }

  gotoStep(gotoStep: number) {
    switch (gotoStep) {
      case 0:
        this.$router.push({ name: 'activate-type' })
        break
      case 1:
        this.$router.push({ name: 'activate-trackpoint' })
        break
      case 2:
        this.$router.push({ name: 'shipping-origin' })
        break
      case 3:
        this.$router.push({ name: 'shipping-destination' })
        break
      case 4:
        this.$router.push({ name: 'shipping-packageInfo' })
        this.isShowArrow = false
        break
    }
  }

  onGotoStep(gotoStep: number) {
    switch (gotoStep) {
      case 0:
        this.$router.replace({ name: 'activate-type' })
        break
      case 1:
        this.$router.replace({ name: 'activate-trackpoint' })
        break
      case 2:
        this.$router.replace({ name: 'shipping-origin' })
        break
      case 3:
        this.$router.replace({ name: 'shipping-destination' })
        break
      case 4:
        this.$router.replace({ name: 'shipping-packageInfo' })
        this.isShowArrow = false
        break
    }
  }

  onChangeDimension(activationData: IActivationData) {
    const { barcodeId, type, origin, destination } = this
    const requestSchema = {
      barcodeId,
      barcodeType: type,
      ...(this.trackpoint && { startTrackPointKeyOverride: this.trackpoint }),
      activationData: {
        ...activationData,
        ...(origin && { origin }),
        ...(destination && { destination }),
      },
    } as IActivateRequestBody

    const filteredObj = filterEmptyActivateData(requestSchema.activationData)
    const requestBody = {
      ...requestSchema,
      activationData: filteredObj,
    } as IActivateRequestBody

    // Send API
    this.activate(requestBody)
  }

  get bulkContext() {
    return this.$store.state.barcode.bulkActivationContext
  }

  private resolveTrackpointForBulk(selectedType: string, candidateTP?: string): string {
    if (!candidateTP) return ''
    const overridableTPs: string[] =
      this.$store.state.project.details?.barcodeTypes?.[selectedType]?.overridableStartTrackPoints || []
    return overridableTPs.includes(candidateTP) ? candidateTP : ''
  }

  async activate(data: IActivateRequestBody) {
    try {
      this.loading = true

      const isBulkExternalActivation = !!this.bulkContext
      if (isBulkExternalActivation) {
        const { returnRoute, operation, extParams, rescanRoute } = this.bulkContext
        await this.$store.dispatch(BULK_ACTIVATE_EXTERNAL, {
          activationData: data.activationData,
          barcodeType: data.barcodeType,
        })
        this.$router.push({
          name: returnRoute,
          params: {
            project: this.$route.params.project,
            barcodeId: this.barcodeId,
            operation,
            rescanRoute,
            extParams: JSON.stringify(extParams),
          },
        })
      } else {
        this.lastActivateRequest = data
        await activateBarcode(data)
        this.$store.dispatch(BARCODE_ACTION_COMPLETE, 'activated')
        this.$router.push({
          name: 'detail',
          params: {
            barcodeId: this.barcodeId,
          },
        })
      }
    } catch (error) {
      if (this.isSkipCustomValidator(error as TError)) {
        this.handleSkippedCustomValidation(error as TError)
        return
      }
      if (hasComparisonError(error as TError)) {
        errorComparisonHandler(error as TError, this.customFields)
      } else {
        errorHandler(error as TError)
      }
    } finally {
      this.loading = false
    }
  }

  isSkipCustomValidator(error: TError) {
    if (typeof error === 'string') {
      if (isJson(error)) {
        const jsonObject = JSON.parse(error)
        return jsonObject.IsSkipCustomValidator ?? false
      }
    }
    return false
  }

  handleSkippedCustomValidation(error: TError, fieldTemplate?: Record<string, string>) {
    const { errorDetails } = processError(error, fieldTemplate)
    this.skipValidationMessage = errorDetails
    this.showSkipValidationDialog = true
  }

  async proceedWithSkippedValidation() {
    if (!this.lastActivateRequest) return
    try {
      this.showSkipValidationDialog = false
      this.loading = true
      const request = { ...this.lastActivateRequest, IsSkipCustomValidator: true }
      await activateBarcode(request)
      this.$store.dispatch(BARCODE_ACTION_COMPLETE, 'activated')
      this.$router.push({
        name: 'detail',
        params: {
          barcodeId: this.barcodeId,
        },
      })
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  get customFields(): Record<string, IActivationCustomField> {
    return this.$store.state.project.details.barcodeTypes[this.barcodeType].activationFields?.customFields
  }

  get barcodeType() {
    return this.$store.state?.barcode?.currentSelectedBarcodeType || this.$store.state?.barcode?.barcode?.barcodeType
  }

  created() {
    const key = this.$route.name.split('-')
    this.currentTab = this.steps.indexOf(key[1])

    if (this.bulkContext) {
      const parentBarcode = this.$store.state.barcode.barcode
      if (parentBarcode) {
        this.type = parentBarcode.barcodeType || ''
        this.origin = parentBarcode.activationData?.origin || null
        this.destination = parentBarcode.activationData?.destination || null
        this.trackpoint = this.resolveTrackpointForBulk(this.type, parentBarcode.currentTrackPointKey)
        if (this.type) {
          this.$store.commit(BARCODE_SELECTED_TYPE, this.type)
        }
      }
    }
  }

  @Watch('$route')
  onRouteUpdate(to: { name: string }) {
    const key = to.name.split('-')
    this.currentTab = this.steps.indexOf(key[1])
  }
}
